import styled from '@emotion/styled';
import {
  h1Style,
  h2Style,
  h3Style,
  h4Style,
  h5Style,
} from '@teetsh/app/src/components/Typography/Headings/styles';
import tw from 'twin.macro';
import {
  backgroundColors,
  borderColors,
} from '@teetsh/app/src/styles/themeConstants';
import { darken } from '@teetsh/app/src/utils/style/darken';
import { specialInsertStyles } from '../plugins/specialInserts/styles';

export const EditorBaseStyle = styled.div`
  ${tw`min-w-0`}
  overflow-wrap: anywhere;

  .fr-view {
    ${tw`min-w-0`}
    overflow-wrap: anywhere;
  }

  ol {
    list-style: decimal;
  }

  ul {
    list-style: disc;
  }

  ul,
  ol {
    padding-left: 1.5rem;
    font-size: inherit;
    margin-top: 5px;
    margin-bottom: 5px;
  }

  .fr-box.fr-basic .fr-element.fr-view {
    font-family: Inter, ui-sans-serif, system-ui, -apple-system,
      BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans,
      sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol,
      Noto Color Emoji;
  }

  p {
    margin-bottom: 0;
  }

  h1 {
    ${h1Style};
  }

  h2 {
    ${h2Style};
  }

  h3 {
    ${h3Style};
  }

  h4 {
    ${h4Style};
  }

  h5 {
    ${h5Style};
  }

  // On fait ça car on gère le spacing plus simplement avec le space-x-* juste plus bas dans la toolbar
  .fr-btn-grp {
    ${tw`m-0!`}
  }

  .fr-toolbar {
    ${tw`px-3! lg:space-x-4! xl:space-x-6!`}
  }
  // Todo blocks use padding to align multi-line text properly
  div[data-type='todo-block'] {
    ${tw`block relative`}
    line-height: 1.5;
    padding-left: 1.5rem;
  }

  div[data-type='todo-block'] > input[type='checkbox'] {
    ${tw`absolute`}
    left: 0;
    top: 0.125rem;
    margin-right: 0;
  }

  input[type='checkbox'] {
    ${tw`h-4 w-4 rounded border-neutral-moderate bg-transparent cursor-pointer`}
    ${tw`focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-brand-moderate focus-visible:ring-offset-2`}
    color: ${backgroundColors['brand-intense']};
  }

  input[type='checkbox']:checked {
    ${tw`h-4 w-4 rounded border-neutral-moderate cursor-pointer`};
    background-color: ${backgroundColors['brand-intense']};
    border-color: ${backgroundColors['brand-intense']};
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
  }

  /* Override Froala's gray theme checkbox styles */
  .gray-theme.fr-popup .fr-checkbox input:checked + span {
    background: ${backgroundColors['brand-intense']} !important;
    border: solid 2px ${backgroundColors['brand-intense']} !important;
  }

  .fr-popup .fr-color-set {
    overflow-y: auto !important;
    max-height: 200px !important;
  }

  .fr-popup.fr-color-popup {
    overflow: visible !important;
  }

  /* Link dialog styling */
  .fr-popup .fr-input-line {
    padding: 15px 0 !important;
    display: flex !important;
    flex-direction: column !important;

    /* Fix the floating label - make it static above the input */

    label {
      position: static !important;
      display: block !important;
      ${tw`text-sm! text-neutral-moderate! mb-1!`}
      transform: none !important;
      width: auto !important;
      font-size: 14px !important;
      background: transparent !important;
      padding: 0 !important;
      top: auto !important;
      left: auto !important;
      order: -1 !important; /* Ensure label appears before input */
    }

    input[type='text'] {
      ${tw`text-sm! py-1! px-2! rounded-md! text-neutral-moderate! placeholder:text-neutral-quiet! placeholder:opacity-70!`}
      border: 1px solid ${borderColors['neutral-quiet']} !important;
      background-color: ${backgroundColors['neutral-white']} !important;
      height: 36px !important;
      width: 100% !important;
      margin: 0 !important;

      &:hover {
        border-color: ${borderColors['brand-intense']} !important;
      }

      &:focus {
        border-color: ${borderColors['brand-intense']} !important;
        outline: none !important;
        box-shadow: 0 0 0 3px ${borderColors['brand-moderate']} !important;
        padding: 3px 7px !important;
      }
    }

    /* Override any default Froala styles for the focused state */

    input.fr-not-empty + label {
      position: static !important;
      transform: none !important;
      font-size: 14px !important;
      top: auto !important;
      left: auto !important;
    }
  }

  .fr-popup .fr-action-buttons button.fr-command {
    ${tw`rounded-lg! py-2! px-4! text-sm! font-medium! text-white! outline-none! transition-all! duration-200!`}
    background-color: ${backgroundColors['brand-intense']} !important;
    border: none !important;
    height: 36px !important;
    min-width: 64px !important;

    &:hover {
      background-color: ${darken(
        0.3,
        backgroundColors['brand-intense'],
      )} !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &:active {
      background-color: ${darken(
        0.4,
        backgroundColors['brand-intense'],
      )} !important;
      transform: translateY(0);
      box-shadow: none;
    }
  }

  /* Override Froala's gray theme checkbox styles */

  .gray-theme.fr-popup .fr-checkbox input:checked + span {
    background: ${backgroundColors['brand-intense']} !important;
    border: solid 2px ${backgroundColors['brand-intense']} !important;
  }

  .gray-theme.fr-popup .fr-checkbox input:not(:checked) + span {
    border: solid 2px ${borderColors['neutral-moderate']} !important;
  }

  .gray-theme.fr-popup .fr-checkbox input:not(:checked):hover + span,
  .gray-theme.fr-popup .fr-checkbox input:not(:checked):focus + span {
    border-color: ${borderColors['brand-intense']} !important;
  }

  /* Special insert styles */
  ${specialInsertStyles}
`;
